/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Box,
	Image,
	Skeleton,
	Tooltip,
} from '@mantine/core';

// Styles
import classes from './TableLastProductions.module.css';

// UI Local components
import { Button } from '../Button';
import { Text } from '../Text';
import { Title } from '../Title';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import OverRangeIcon from '@assets/icons/over-range.svg';
import { ProductionQuantityCheck } from '@config/constants/enum';
import { TProductionPopulatedParsed } from '@features/sourcing/productions/types';
import { StatusSwitch } from '../StatusSwitch';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type TTableLastProductionsProps = {
	data: TProductionPopulatedParsed[];
	onSeeMoreClick?: () => void;
	isLoading: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const TableLastProductions = ({
	data,
	onSeeMoreClick,
	isLoading,
}: TTableLastProductionsProps) => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	/* ******************************** RENDERING ******************************* */

	if (isLoading)
		return (
			<Box className={classes.container}>
				<Box className={classes.header}>
					<Title
						order={6}
						c="var(--mantine-color-igluPrimary-9)"
					>
						{t('global.lastProductions')}
					</Title>
					<Button
						onClick={onSeeMoreClick}
						variant="outline"
					>
						{t('global.viewMore')}
					</Button>
				</Box>
				<Box className={classes.body}>
					<table className={classes.tableStyle}>
						<thead>
							<tr>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.project')}
									</Text>
								</th>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.activityReportDate')}
									</Text>
								</th>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.production')}
									</Text>
								</th>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.status')}
									</Text>
								</th>
								<th className={classes.thStyle}>
									<Text
										variant="caption"
										c="var(--mantine-color-igluTextSecondary-9)"
									>
										{t('global.qtyAlert')}
									</Text>
								</th>
							</tr>
						</thead>
						<tbody>
							{[...new Array(4)].map(
								(
									_row: TProductionPopulatedParsed,
									index
								) => (
									<tr key={index}>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'160px'}
												radius="md"
											/>
										</td>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'160px'}
												radius="md"
											/>
										</td>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'130px'}
												radius="md"
											/>
										</td>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'60px'}
												radius="md"
											/>
										</td>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'60px'}
												radius="md"
											/>
										</td>
										<td className={classes.tdStyle}>
											<Skeleton
												height={'20px'}
												width={'60px'}
												radius="md"
											/>
										</td>
									</tr>
								)
							)}
						</tbody>
					</table>
				</Box>
			</Box>
		);

	return (
		<Box className={classes.container}>
			<Box className={classes.header}>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{t('global.lastProductions')}
				</Title>
				<Button
					onClick={onSeeMoreClick}
					variant="outline"
				>
					{t('global.viewMore')}
				</Button>
			</Box>
			<Box className={classes.body}>
				<table className={classes.tableStyle}>
					<thead>
						<tr>
							<th className={classes.thStyle}>
								<Text
									variant="caption"
									c="var(--mantine-color-igluTextSecondary-9)"
								>
									{t('global.project')}
								</Text>
							</th>
							<th className={classes.thStyle}>
								<Text
									variant="caption"
									c="var(--mantine-color-igluTextSecondary-9)"
								>
									{t('global.startDate')}
								</Text>
							</th>
							<th className={classes.thStyle}>
								<Text
									variant="caption"
									c="var(--mantine-color-igluTextSecondary-9)"
								>
									{t('global.quantity')}
								</Text>
							</th>
							<th className={classes.thStyle}>
								<Text
									variant="caption"
									c="var(--mantine-color-igluTextSecondary-9)"
								>
									{t('global.status')}
								</Text>
							</th>
							<th className={classes.thStyle}>
								<Text
									variant="caption"
									c="var(--mantine-color-igluTextSecondary-9)"
								>
									{t('global.qtyAlert')}
								</Text>
							</th>
						</tr>
					</thead>
					<tbody>
						{Array.isArray(data) &&
							data.map(
								(
									row: TProductionPopulatedParsed,
									index
								) => (
									<tr key={index}>
										<td className={classes.tdStyle}>
											<Text
												variant="subtitle1"
												c="var(--mantine-color-igluTextPrimary-9)"
											>
												{row.project}
											</Text>
										</td>
										<td className={classes.tdStyle}>
											<Text
												variant="body2"
												c="var(--mantine-color-igluTextPrimary-9)"
											>
												{row.startDate}
											</Text>
										</td>
										<td className={classes.tdStyle}>
											<Text
												variant="body2"
												c="var(--mantine-color-igluTextPrimary-9)"
											>
												{row.production}
											</Text>
										</td>
										<td className={classes.tdStyle}>
											<StatusSwitch
												active={row.verified}
												activeLabel={t('global.verified')}
												inActiveLabel={t(
													'global.notVerified'
												)}
												onChange={() => {}}
											/>
										</td>
										<td className={classes.tdStyle}>
											{row.quantityCheck ==
												ProductionQuantityCheck.OVER_RANGE && (
												<Tooltip
													label={t('global.overRange')}
												>
													<Image
														className={
															classes.tdOverrange
														}
														h={'20px'}
														w={'20px'}
														src={OverRangeIcon}
													/>
												</Tooltip>
											)}
											{row.quantityCheck ==
												ProductionQuantityCheck.UNDER_RANGE && (
												<Tooltip
													label={t('global.underRange')}
												>
													<Image
														style={{
															transform: 'rotate(180deg)',
														}}
														className={
															classes.tdOverrange
														}
														h={'20px'}
														w={'20px'}
														src={OverRangeIcon}
													/>
												</Tooltip>
											)}
										</td>
									</tr>
								)
							)}
					</tbody>
				</table>
			</Box>
			{data &&
				Array.isArray(data) &&
				data.length == 0 && (
					<Box
						className={classes.containerTextsNoRecords}
					>
						<Image
							src={NoRecordsIcon}
							className={classes.iconNoRecords}
						/>
						<Text
							variant="caption"
							c="var(--mantine-color-igluTextSecondary-9)"
						>
							{t('global.datatable.noResultsFound')}
						</Text>
					</Box>
				)}
		</Box>
	);
};
